import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Clock, Check, CreditCard, HelpCircle, X } from 'lucide-react';

const TrialExpiredPage = () => {
  const location = useLocation();
  const message = location.state?.message;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4 py-12">
      <div className="max-w-5xl w-full">
        
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="flex justify-center mb-4 sm:mb-6">
            <div className="bg-blue-600 rounded-full p-4 sm:p-6">
              <Clock className="h-12 w-12 sm:h-16 sm:w-16 text-white" />
            </div>
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 sm:mb-3 px-4">
            Your Trial Has Ended
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
            Ready to keep growing your business? Choose a plan and pick up right where you left off.
          </p>
        </div>

        {/* Data Safety Notice */}
        <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8 mx-4 sm:mx-0">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <Check className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm sm:text-base font-semibold text-blue-900 mb-1">Your data is safe</h3>
              <p className="text-sm sm:text-base text-blue-800">
                All your customer data, jobs, and invoices are securely stored. Subscribe now to regain instant access to everything.
              </p>
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8 px-4 sm:px-0">
          
          {/* Solo Plan */}
          <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-gray-300 hover:shadow-lg transition-all">
            <div className="text-center mb-4 sm:mb-6">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Solo</h3>
              <div className="mb-3">
                <span className="text-4xl sm:text-5xl font-bold text-gray-900">$19</span>
                <span className="text-gray-600 text-base sm:text-lg">/month</span>
              </div>
              <p className="text-sm text-gray-600">Perfect for owner-operators</p>
            </div>
            
            <ul className="space-y-2 sm:space-y-3 mb-6">
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">1 user</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">Unlimited customers & jobs</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">Invoicing & expenses</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">SuddySnap™ photo docs</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">Schedule C tax reports</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">Quarterly tax estimates</span>
              </li>
              <li className="flex items-start">
                <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-300 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-400">Route optimization</span>
              </li>
              <li className="flex items-start">
                <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-300 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-400">P&L reports</span>
              </li>
            </ul>
            
            <Link
              to="/subscribe?plan=solo"
              className="block w-full bg-gray-900 text-white text-center py-3 rounded-lg text-base font-semibold hover:bg-gray-800 transition-colors"
            >
              Choose Solo
            </Link>
          </div>

          {/* Pro Plan - Featured */}
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-6 text-white relative shadow-xl transform md:-translate-y-2">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <span className="bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-xs sm:text-sm font-bold shadow-lg whitespace-nowrap">
                ⭐ MOST POPULAR
              </span>
            </div>
            
            <div className="text-center mb-4 sm:mb-6 mt-2">
              <h3 className="text-xl sm:text-2xl font-bold mb-2">Pro</h3>
              <div className="mb-3">
                <span className="text-4xl sm:text-5xl font-bold">$49</span>
                <span className="text-blue-100 text-base sm:text-lg">/month</span>
              </div>
              <p className="text-sm text-blue-100">For growing teams</p>
            </div>
            
            <ul className="space-y-2 sm:space-y-3 mb-6">
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-300 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm">Up to 15 team members</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-300 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm">Everything in Solo, plus:</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-300 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm font-semibold">SuddyRoute™ optimization</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-300 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm">P&L reports & analytics</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-300 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm">Customer portal</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-300 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm">Automated emails</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-300 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm">QuickBooks sync</span>
              </li>
              <li className="flex items-start">
                <X className="h-4 w-4 sm:h-5 sm:w-5 text-blue-300 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-blue-200">Custom branding</span>
              </li>
            </ul>
            
            <Link
              to="/subscribe?plan=pro"
              className="block w-full bg-white text-blue-600 text-center py-3 rounded-lg text-base font-bold hover:bg-blue-50 transition-colors shadow-lg"
            >
              Choose Pro
            </Link>
          </div>

          {/* Enterprise Plan */}
          <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-purple-300 hover:shadow-lg transition-all">
            <div className="text-center mb-4 sm:mb-6">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Enterprise</h3>
              <div className="mb-3">
                <span className="text-4xl sm:text-5xl font-bold text-gray-900">$129</span>
                <span className="text-gray-600 text-base sm:text-lg">/month</span>
              </div>
              <p className="text-sm text-gray-600">For scaling operations</p>
            </div>
            
            <ul className="space-y-2 sm:space-y-3 mb-6">
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700 font-semibold">Unlimited team members</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">Everything in Pro, plus:</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">Multi-location support</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">Custom branding</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">Custom email templates</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">Advanced analytics</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">API access</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">Priority support</span>
              </li>
            </ul>
            
            <Link
              to="/subscribe?plan=enterprise"
              className="block w-full bg-purple-600 text-white text-center py-3 rounded-lg text-base font-semibold hover:bg-purple-700 transition-colors"
            >
              Choose Enterprise
            </Link>
          </div>
        </div>

        {/* Bottom Actions */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center mb-6 px-4">
          <Link
            to="/pricing"
            className="inline-flex items-center justify-center px-4 sm:px-6 py-3 border-2 border-gray-300 text-sm sm:text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <CreditCard className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
            Compare All Plans
          </Link>
          
          <a  href="mailto:support@suddyflow.com"
            className="inline-flex items-center justify-center px-4 sm:px-6 py-3 border-2 border-gray-300 text-sm sm:text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <HelpCircle className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
            Contact Support
          </a>
        </div>

        {/* Footer */}
        <div className="text-center px-4">
          <p className="text-gray-500 text-xs sm:text-sm mb-3">
            🇺🇸 Veteran-owned business • No long-term contracts • Cancel anytime
          </p>
          <p className="text-gray-400 text-xs sm:text-sm">
            Questions? Email{' '}
            <a href="mailto:support@suddyflow.com" className="text-blue-600 hover:text-blue-700">
              support@suddyflow.com
            </a>
          </p>
          <p className="mt-3 sm:mt-4">
            <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium text-sm sm:text-base">
              ← Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TrialExpiredPage;